import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { applySEO, SITE_URL, breadcrumbSchema } from "@/lib/seo";

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", search: "" });

  useEffect(() => {
    applySEO({
      title: "Journal — Asymmetry Edits",
      description: "Stories behind every stitch. Fashion editorials, craftsmanship notes, and behind-the-scenes from the Asymmetry Edits atelier.",
      canonical: `${SITE_URL}/journal`,
      jsonLd: [
        { id:"ld-journal-crumb", data: breadcrumbSchema([{name:"Home",url:"/"},{name:"Journal",url:"/journal"}]) },
        { id:"ld-journal-blog", data: { "@context":"https://schema.org","@type":"Blog","@id":`${SITE_URL}/journal#blog`,name:"Asymmetry Edits Journal",url:`${SITE_URL}/journal`,publisher:{"@id":`${SITE_URL}/#organization`}}},
      ],
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.category) params.category = filters.category;
    if (filters.search) params.search = filters.search;
    Promise.all([
      api.get("/journal/articles", { params }),
      api.get("/journal/categories"),
    ]).then(([a,c])=>{ setArticles(a.data); setCats(c.data); }).finally(()=>setLoading(false));
  }, [filters]);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main data-testid="journal-page" className="pt-28 md:pt-32">
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-b border-white/8">
          <div className="eyebrow mb-4">Editorial</div>
          <h1 className="font-serif text-white text-5xl md:text-7xl leading-[0.95]">Journal.</h1>
          <p className="mt-6 max-w-xl text-white/60 font-light text-base md:text-lg">Stories behind every stitch.</p>
        </section>

        <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 space-y-12">
            {loading ? <div className="text-white/50">Loading…</div> : articles.length===0 ? (
              <div className="text-center py-20 text-white/55"><h3 className="font-serif text-3xl mb-2 text-white">No articles yet.</h3><p>New stories arrive soon.</p></div>
            ) : (
              <>
                {featured && (
                  <Link to={`/journal/${featured.slug}`} data-testid={`article-featured-${featured.id}`} className="group block">
                    <div className="aspect-[16/9] overflow-hidden bg-midnight-panel mb-6"><img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"/></div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">{featured.category} · {new Date(featured.created_at).toLocaleDateString()} · {featured.reading_time_minutes} min read</div>
                    <h2 className="font-serif text-white text-3xl md:text-5xl leading-[1] mt-3">{featured.title}</h2>
                    <p className="mt-3 text-white/60 max-w-2xl">{featured.excerpt}</p>
                    <div className="mt-4 text-[11px] tracking-[0.32em] uppercase text-silver group-hover:text-white">Continue reading →</div>
                  </Link>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 border-t border-white/8 pt-10">
                  {rest.map(a => (
                    <Link key={a.id} to={`/journal/${a.slug}`} data-testid={`article-card-${a.id}`} className="group block">
                      <div className="aspect-[4/3] overflow-hidden bg-midnight-panel mb-4"><img src={a.cover_image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/></div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">{a.category} · {a.reading_time_minutes} min</div>
                      <h3 className="font-serif text-white text-2xl leading-tight mt-2">{a.title}</h3>
                      <p className="mt-2 text-white/55 text-sm">{a.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-10">
            <div>
              <div className="eyebrow mb-3">Search</div>
              <input type="search" placeholder="Search stories…" value={filters.search} onChange={(e)=>setFilters({...filters,search:e.target.value})} className="lux-input w-full" data-testid="journal-search"/>
            </div>
            {cats.length>0 && <div>
              <div className="eyebrow mb-3">Categories</div>
              <ul className="space-y-2">
                <li><button onClick={()=>setFilters({...filters,category:""})} className={`text-sm ${!filters.category?"text-white":"text-white/60 hover:text-white"}`}>All</button></li>
                {cats.map(c=><li key={c}><button onClick={()=>setFilters({...filters,category:c})} className={`text-sm ${filters.category===c?"text-white":"text-white/60 hover:text-white"}`}>{c}</button></li>)}
              </ul>
            </div>}
            <div>
              <div className="eyebrow mb-3">Recent</div>
              <ul className="space-y-3">
                {articles.slice(0,5).map(a=><li key={a.id}><Link to={`/journal/${a.slug}`} className="font-serif text-white/80 hover:text-white text-base leading-tight block">{a.title}</Link><div className="text-[10px] text-white/40 tracking-[0.25em] uppercase mt-1">{a.category}</div></li>)}
              </ul>
            </div>
          </aside>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
